/**
 * Parallel filing upload: fetches filing details and uploads documents to
 * Supabase Storage for a list of filings, all in parallel.
 *
 * Extracted from DocketSearchContent.uploadAllFiles so it can be reused
 * on the proceedings page.
 */

import * as Sentry from '@sentry/react';
import {
  checkDocumentExists,
  checkDocumentExistsInStorage,
  recordDocumentUpload,
} from './documentTrackingService';
import { getFilingDetails } from './ecfsBrowserClient';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface MinimalFiling {
  id_submission: string;
  filer_name?: string | null;
  filers?: Array<{ name: string }>;
  submissiontype?: { short?: string; description?: string };
  filing_type?: string | null;
  express_comment?: number | boolean;
  text_data?: string | null;
  date_received?: string | null;
  date_submission?: string | null;
}

export interface ParallelUploadResult {
  succeeded: number;
  failed: number;
  skipped: number;
}

/**
 * Per-filing outcome returned by {@link uploadFilingsParallelWithResults}.
 *
 * - `uploaded`: at least one document for the filing is confirmed in storage
 *   (either newly uploaded or already present).
 * - `unavailable`: the filing's document(s) were rejected by upload-ecfs-files
 *   for reasons that will not change on retry (size > 50 MB, unsupported
 *   extension, spam filer, express-comment rejection). The UI should surface
 *   these so users cannot start a summary that will silently skip them.
 * - `failed`: transient-looking failure (network, 5xx, details fetch error).
 */
export type FilingUploadOutcome =
  | { submissionId: string; status: 'uploaded' }
  | { submissionId: string; status: 'unavailable'; reason: string }
  | { submissionId: string; status: 'failed'; reason: string }
  | { submissionId: string; status: 'skipped'; reason: string };

export interface ParallelUploadDetailedResult extends ParallelUploadResult {
  outcomes: FilingUploadOutcome[];
}

function classifyUploadError(status: number, errorText: string): 'unavailable' | 'failed' {
  const text = (errorText || '').toLowerCase();
  if (status === 400) {
    if (
      text.includes('size') ||
      text.includes('exceed') ||
      text.includes('not allowed') ||
      text.includes('file type') ||
      text.includes('extension') ||
      text.includes('spam') ||
      text.includes('express')
    ) {
      return 'unavailable';
    }
  }
  return 'failed';
}

function authHeaders() {
  return {
    Authorization: `Bearer ${ANON_KEY}`,
    'Content-Type': 'application/json',
  };
}

function sanitize(str: string): string {
  return str
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^\w\-_.]/g, '')
    .substring(0, 100);
}

async function uploadSingleFiling(
  filing: MinimalFiling,
  docketNumber: string
): Promise<FilingUploadOutcome> {
  const submissionId = filing.id_submission;

  // Skip express comments
  const isExpress =
    filing.express_comment === 1 ||
    filing.express_comment === true ||
    (filing.express_comment as unknown) === '1';
  if (isExpress) {
    return { submissionId, status: 'skipped', reason: 'express-comment' };
  }

  try {
    const details = await getFilingDetails(submissionId);
    if (!details || details.error) {
      return {
        submissionId,
        status: 'failed',
        reason: details?.error || 'Failed to fetch filing details from ECFS',
      };
    }

    const documents: any[] = details.documents || [];
    if (documents.length === 0) {
      return { submissionId, status: 'skipped', reason: 'no-documents' };
    }

    const filerName =
      filing.filers?.[0]?.name || details.filers?.[0]?.name || filing.filer_name || 'Unknown_Filer';
    const filingType =
      filing.submissiontype?.short ||
      details.submissiontype?.short ||
      filing.filing_type ||
      'UNKNOWN';

    if (filingType === 'EXPRESS-COMMENT' || filingType === 'EXPRESS_COMMENT') {
      return { submissionId, status: 'skipped', reason: 'express-comment' };
    }

    let anyUploaded = false;
    // Permanent rejections override generic failures — if even one document was
    // rejected for size/extension/spam, classify the filing as unavailable so
    // users see why.
    let unavailableReason: string | null = null;
    let failureReason: string | null = null;

    for (const doc of documents) {
      const src = doc.src || doc.url || doc.file_url || doc.document_url;
      if (!src) continue;

      const fileName = doc.filename || src.split('/').pop() || 'document.pdf';

      const existsInDb = await checkDocumentExists(submissionId, docketNumber, fileName);
      // Always verify against storage — the DB tracking table is only written by the
      // frontend upload path. Files ingested by the backend pipeline (ingest-worker,
      // upload-ecfs-files triggered server-side) are in storage but absent from
      // uploaded_documents, so a DB-only check causes every page visit to re-upload
      // every previously ingested document.
      const existsInStorage = await checkDocumentExistsInStorage(
        submissionId,
        docketNumber,
        filingType,
        fileName
      );
      if (existsInStorage) {
        anyUploaded = true;
        // Backfill the DB tracking record so the FilingsSection repair pass
        // doesn't need to build the full storage index on subsequent page loads.
        // Fire-and-forget — a failure here is non-fatal (storage is the source of truth).
        if (!existsInDb) {
          const storagePath = `ecfs/${docketNumber}/${sanitize(filingType)}/${submissionId}___${sanitize(filerName)}___${sanitize(fileName)}`;
          recordDocumentUpload({
            submission_id: submissionId,
            docket_number: docketNumber,
            file_name: fileName,
            filing_type: filingType,
            storage_path: storagePath,
            file_size: null,
            is_express_comment: false,
            source_url: src,
          }).catch((err) =>
            console.warn(
              `[parallel-upload] backfill recordDocumentUpload failed (docket=${docketNumber}, submission=${submissionId}, file=${fileName})`,
              err
            )
          );
        }
        continue;
      }
      if (existsInDb && !existsInStorage) {
        console.info(
          `[parallel-upload] db_hit_storage_miss -> reupload (docket=${docketNumber}, submission=${submissionId}, file=${fileName})`
        );
      }

      const uploadUrl = `${SUPABASE_URL}/functions/v1/upload-ecfs-files`;
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          src,
          docketNumber,
          filerName,
          fileName,
          submissionId,
          filingType,
          expressComment: filing.express_comment || null,
        }),
      });

      if (res.ok) {
        anyUploaded = true;
        // Parse response and persist an uploaded_documents row so other flows
        // (Filings tab retrievability check, personalized summary retrieval)
        // can see the file without listing storage.
        try {
          const body = await res.json();
          if (body?.status === 'success' && body?.path) {
            await recordDocumentUpload({
              submission_id: submissionId,
              docket_number: docketNumber,
              file_name: fileName,
              filing_type: filingType,
              storage_path: body.path,
              file_size: typeof body.size === 'number' ? body.size : null,
              is_express_comment: false,
              source_url: src,
            });
          }
        } catch (recordErr) {
          console.warn(
            `[parallel-upload] recordDocumentUpload failed (docket=${docketNumber}, submission=${submissionId}, file=${fileName})`,
            recordErr
          );
        }
      } else {
        const errText = await res.text().catch(() => '');
        const classification = classifyUploadError(res.status, errText);
        const reasonFromBody = (() => {
          try {
            const parsed = JSON.parse(errText);
            return parsed?.error || parsed?.message || null;
          } catch {
            return null;
          }
        })();
        const reason = reasonFromBody || errText || `HTTP ${res.status}`;

        if (classification === 'unavailable' && !unavailableReason) {
          unavailableReason = reason;
        }
        if (classification === 'failed' && !failureReason) {
          failureReason = reason;
        }

        console.warn(
          `[parallel-upload] upload-ecfs-files ${classification} (docket=${docketNumber}, submission=${submissionId}, status=${res.status})`,
          errText
        );
        Sentry.addBreadcrumb({
          category: 'edge-fn',
          level: 'warning',
          message: 'upload-ecfs-files non-2xx',
          data: { docketNumber, submissionId, status: res.status, fileName, classification },
        });
      }
    }

    if (anyUploaded) return { submissionId, status: 'uploaded' };
    if (unavailableReason) {
      return { submissionId, status: 'unavailable', reason: unavailableReason };
    }
    return {
      submissionId,
      status: 'failed',
      reason: failureReason || 'No documents uploaded',
    };
  } catch (err) {
    console.error(`Upload failed for ${submissionId}:`, err);
    return {
      submissionId,
      status: 'failed',
      reason: err instanceof Error ? err.message : 'Upload failed',
    };
  }
}

/**
 * Upload documents for a list of filings in parallel.
 * Fetches filing details directly from ecfsapi.fcc.gov in the browser
 * (see ./ecfsBrowserClient) and hands the documents[].src URLs to the
 * upload-ecfs-files edge function for download-and-store.
 */
export async function uploadFilingsParallel(
  filings: MinimalFiling[],
  docketNumber: string,
  onProgress?: (completed: number, total: number) => void
): Promise<ParallelUploadResult> {
  const { succeeded, failed, skipped } = await uploadFilingsParallelWithResults(
    filings,
    docketNumber,
    onProgress
  );
  return { succeeded, failed, skipped };
}

/**
 * Same contract as {@link uploadFilingsParallel} but also returns a per-filing
 * outcome list so callers (e.g. the Filings tab retrievability check) can
 * distinguish transient failures from permanent rejections without re-running
 * the upload.
 */
export async function uploadFilingsParallelWithResults(
  filings: MinimalFiling[],
  docketNumber: string,
  onProgress?: (completed: number, total: number) => void
): Promise<ParallelUploadDetailedResult> {
  const total = filings.length;
  let completed = 0;
  const outcomes: FilingUploadOutcome[] = [];
  const result: ParallelUploadDetailedResult = {
    succeeded: 0,
    failed: 0,
    skipped: 0,
    outcomes,
  };

  const promises = filings.map(async (filing) => {
    let outcome: FilingUploadOutcome;
    try {
      outcome = await uploadSingleFiling(filing, docketNumber);
    } catch (err) {
      console.error(
        `[parallel-upload] unhandled error for submission=${filing.id_submission}:`,
        err
      );
      outcome = {
        submissionId: filing.id_submission,
        status: 'failed',
        reason: err instanceof Error ? err.message : 'Unhandled error',
      };
    }

    completed++;
    outcomes.push(outcome);
    switch (outcome.status) {
      case 'uploaded':
        result.succeeded++;
        break;
      case 'skipped':
        result.skipped++;
        break;
      case 'unavailable':
      case 'failed':
      default:
        result.failed++;
        break;
    }
    onProgress?.(completed, total);
    return outcome;
  });

  await Promise.allSettled(promises);
  return result;
}
